function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value)
}

/** Flat refresh/PATCH body: amenities/highlights live on the root, not under `pd`. */
function isFlatPropertyDetailRecord(obj) {
  if (!isPlainObject(obj)) return false
  if (obj.pd != null || obj.c != null) return false
  return (
    obj.id != null ||
    Array.isArray(obj.highlights) ||
    Array.isArray(obj.amenities) ||
    typeof obj.location === "string" ||
    typeof obj.overview === "string" ||
    isPlainObject(obj.hotelInformation) ||
    isPlainObject(obj.hotel_information) ||
    isPlainObject(obj.basicInfo) ||
    isPlainObject(obj.basic_info) ||
    isPlainObject(obj.nearby) ||
    isPlainObject(obj.gallery) ||
    Array.isArray(obj.foodAndBeverages) ||
    Array.isArray(obj.food_and_beverage)
  )
}

function asPdC(pd, c) {
  return { pd: isPlainObject(pd) ? pd : null, c: isPlainObject(c) ? c : null }
}

/** Normalize GET `{ pd, c }`, `{ data: { pd, c } }`, or a flat property-detail record to `{ pd, c }`. */
export function unwrapPropertyDetailResponse(res) {
  if (!isPlainObject(res)) return asPdC(null, null)
  if (isPlainObject(res.data) && (res.data.pd != null || res.data.c != null)) {
    return asPdC(res.data.pd, res.data.c)
  }
  if (res.pd != null || res.c != null) return asPdC(res.pd, res.c)
  if (isFlatPropertyDetailRecord(res)) return asPdC(res, null)
  if (isFlatPropertyDetailRecord(res.data)) return asPdC(res.data, null)
  return asPdC(null, null)
}

/** Keep `c` from GET when refresh/PATCH returns only the property-detail record. */
export function mergePropertyDetailRaw(prev, incoming) {
  const next = unwrapPropertyDetailResponse(incoming)
  const previous = unwrapPropertyDetailResponse(prev)
  return {
    pd: next.pd ?? previous.pd ?? null,
    c: next.c ?? previous.c ?? null,
  }
}

function nonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0
}

function arrayHasContent(arr) {
  return Array.isArray(arr) && arr.some((item) => nonEmptyString(String(item ?? "")))
}

function trimStringArray(arr) {
  if (!Array.isArray(arr)) return []
  return arr.map((item) => String(item ?? "").trim()).filter(Boolean)
}

function pickNonEmptyString(...values) {
  for (const value of values) {
    if (value == null) continue
    const text = String(value).trim()
    if (text) return text
  }
  return ""
}

function toOptionalNumber(value) {
  if (value == null || value === "") return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Normalize `pd.basicInfo` (snake_case GET) plus legacy top-level fields. */
export function getBasicInfoFromPd(pd) {
  const bi =
    (pd?.basicInfo && typeof pd.basicInfo === "object" ? pd.basicInfo : null) ||
    (pd?.basic_info && typeof pd.basic_info === "object" ? pd.basic_info : null) ||
    {}

  const hotelName = pickNonEmptyString(
    bi.hotel_name,
    bi.hotelName,
    pd?.hotel_name,
    pd?.hotelName,
    pd?.propertyName,
    pd?.name,
  )
  const phone = pickNonEmptyString(
    bi.phone_number,
    bi.phoneNumber,
    bi.phone,
    pd?.phone_number,
    pd?.phoneNumber,
    pd?.phone,
  )
  const addressFull = pickNonEmptyString(bi.address_full, bi.addressFull)
  const addressShort = pickNonEmptyString(bi.address_short, bi.addressShort)
  const locationFromObject =
    pd?.location && typeof pd.location === "object" && !Array.isArray(pd.location)
      ? pickNonEmptyString(pd.location.address, pd.location.address_full, pd.location.addressFull)
      : ""
  const location = pickNonEmptyString(
    typeof pd?.location === "string" ? pd.location : "",
    locationFromObject,
    addressFull,
    addressShort,
  )
  const reviewScoreText = pickNonEmptyString(
    bi.review_score_text,
    bi.reviewScoreText,
    pd?.review_score_text,
    pd?.reviewScoreText,
  )

  return {
    hotelName,
    phone,
    addressFull,
    addressShort,
    location,
    starRating: toOptionalNumber(
      bi.star_rating ?? bi.starRating ?? pd?.star_rating ?? pd?.starRating ?? pd?.stars ?? pd?.rating,
    ),
    reviewCount: toOptionalNumber(
      bi.review_count ?? bi.reviewCount ?? pd?.review_count ?? pd?.reviewCount ?? pd?.reviewsCount,
    ),
    reviewScoreText,
  }
}

function highlightLabel(item) {
  if (typeof item === "string") return item.trim()
  if (isPlainObject(item)) {
    return pickNonEmptyString(item.label, item.name, item.text, item.title)
  }
  return ""
}

/** GET `highlights` — strings or `{ label }` objects from scrape/re-scan. */
export function getHighlightsFromPd(pd) {
  if (!pd || typeof pd !== "object" || !Array.isArray(pd.highlights)) return []
  return pd.highlights.map(highlightLabel).filter(Boolean)
}

export function getOverviewFromPd(pd) {
  if (!pd || typeof pd !== "object") return ""
  if (typeof pd.overview === "string") return pd.overview.trim()
  if (isPlainObject(pd.overview)) return pickNonEmptyString(pd.overview.text, pd.overview.overview)
  return ""
}

export function getHotelInformationFromPd(pd) {
  if (!pd || typeof pd !== "object") return {}
  if (isPlainObject(pd.hotelInformation)) return pd.hotelInformation
  if (isPlainObject(pd.hotel_information)) return pd.hotel_information
  return {}
}

function formatNearbyDistance(row) {
  if (row == null) return ""
  const rawDistance = row.distance
  if (rawDistance != null && String(rawDistance).trim() !== "") {
    return String(rawDistance).trim()
  }
  const value = row.distance_value ?? row.distanceValue
  if (value == null || value === "") return ""
  const n = Number(value)
  const num = Number.isFinite(n) ? String(n) : String(value).trim()
  const unit = pickNonEmptyString(row.distance_unit, row.distanceUnit)
  if (!unit) return num
  return `${num} ${unit.toUpperCase()}`
}

function normalizeNearbyRow(row) {
  if (typeof row === "string") {
    const name = row.trim()
    return name ? { name, distance: "" } : null
  }
  if (!isPlainObject(row)) return null
  const name = pickNonEmptyString(row.name, row.label, row.airport, row.title, row.attraction)
  if (!name) return null
  return { name, distance: formatNearbyDistance(row) }
}

function firstNearbyList(...lists) {
  for (const list of lists) {
    if (!Array.isArray(list) || list.length === 0) continue
    const rows = list.map(normalizeNearbyRow).filter(Boolean)
    if (rows.length > 0) return rows
  }
  return []
}

/** Airports and attractions from `nearby` (scrape) or legacy GET field names. */
export function getNearbyFromPd(pd) {
  const nearby = isPlainObject(pd?.nearby) ? pd.nearby : {}
  return {
    airports: firstNearbyList(
      nearby.airports,
      pd?.nearestAirports,
      pd?.nearest_airports,
      pd?.airports,
    ),
    attractions: firstNearbyList(
      nearby.attractions,
      nearby.local_attractions,
      nearby.localAttractions,
      pd?.localAttractions,
      pd?.local_attractions,
      pd?.attractions,
    ),
  }
}

/** @deprecated Use getHighlightsFromPd */
export function getKeyHighlightsFromPd(pd) {
  return getHighlightsFromPd(pd)
}

export function getAmenitiesFromPd(pd) {
  if (!pd || typeof pd !== "object") return []
  return trimStringArray(pd.amenities)
}

/** True when the API returned a property-detail record with an id or meaningful content. */
export function hasPropertyDetailData(raw) {
  const data = unwrapPropertyDetailResponse(raw)
  const pd = data?.pd
  if (!pd || typeof pd !== "object") return false
  if (pd.id != null && pd.id !== "") return true

  if (nonEmptyString(typeof pd.location === "string" ? pd.location : "") || nonEmptyString(getOverviewFromPd(pd))) {
    return true
  }
  const basicInfo = getBasicInfoFromPd(pd)
  if (
    nonEmptyString(basicInfo.phone) ||
    nonEmptyString(basicInfo.location) ||
    nonEmptyString(basicInfo.reviewScoreText) ||
    basicInfo.starRating != null ||
    basicInfo.reviewCount != null
  ) {
    return true
  }
  if (arrayHasContent(getHighlightsFromPd(pd)) || arrayHasContent(getAmenitiesFromPd(pd))) return true

  const gallery = pd.gallery && typeof pd.gallery === "object" ? pd.gallery : {}
  const images = Array.isArray(gallery.images) ? gallery.images : []
  if (nonEmptyString(gallery.hero_image) || images.some((u) => nonEmptyString(u))) return true

  const hi = getHotelInformationFromPd(pd)
  if (
    nonEmptyString(hi.check_in_time) ||
    nonEmptyString(hi.check_out_time) ||
    nonEmptyString(hi.pet_policy) ||
    nonEmptyString(hi.child_policy)
  ) {
    return true
  }

  if (Array.isArray(pd.foodAndBeverages) && pd.foodAndBeverages.length > 0) return true
  if (Array.isArray(pd.food_and_beverage) && pd.food_and_beverage.length > 0) return true
  const fb = pd.foodAndBeverage
  if (fb && typeof fb === "object" && !Array.isArray(fb)) {
    if (nonEmptyString(fb.venue_name) || nonEmptyString(fb.description) || nonEmptyString(fb.venue_type)) {
      return true
    }
  }
  const nearby = getNearbyFromPd(pd)
  if (nearby.airports.length > 0 || nearby.attractions.length > 0) return true

  return false
}

/** Property-detail id for PATCH, when present. */
export function getPropertyDetailId(raw) {
  const pd = unwrapPropertyDetailResponse(raw)?.pd
  if (pd?.id == null || pd.id === "") return null
  return pd.id
}
